<template>
  <!-- The signed-in window (SIGNED_IN_PLAN 3.1):
         Sidebar (210 pointer / 280 touch) | Rail (64) | nothing (phone, or
         a "full" route)
         [SessionListColumn 300: an open session, 1100 px and up, pointer]
         the pane: <router-view :route="paneRoute">, never keyed
         PaneFoot: PlayerBar from 700 px, MiniPlayer under it
         OverlayHost: Settings window, Inbox sheet, sheets, account menu
       The document scrolls the pane; the sidebar, rail and list column are
       sticky. -->
  <div class="app-shell" :class="[`shell--${mode}`, `size--${size}`]">
    <Sidebar v-if="showSidebar" :selected-place="selectedPlace" />
    <Rail v-else-if="showRail" :selected-place="selectedPlace" :can-expand="size === 'desktop' && collapsed" />
    <SessionListColumn v-if="showListColumn" :place-id="listPlace" :open-name="openSessionName" />
    <div class="pane-column">
      <PhoneNav v-if="genericPhoneNav" :back="paneRoute.meta.phoneBack || 'Sessions'" :title="paneTitle" />
      <main class="pane">
        <router-view v-if="paneRoute" :route="paneRoute" />
      </main>
      <PaneFoot />
    </div>
    <OverlayHost />
  </div>
</template>

<script>
import { computed } from "vue";
import { loadRouteLocation } from "vue-router";
import Sidebar from "./Sidebar.vue";
import Rail from "./Rail.vue";
import SessionListColumn from "./SessionListColumn.vue";
import PaneFoot from "./PaneFoot.vue";
import OverlayHost from "./OverlayHost.vue";
import PhoneNav from "@/components/kit/PhoneNav.vue";
import { installDensity } from "./density.js";
import { installShortcuts, setShortcutEnv } from "./shortcuts.js";
import {
  lastSessionListPath,
  overlayKind,
  recordPane,
  refreshInboxBadge,
  setCollapsed,
  setDensity,
  shellMode,
  shellState,
} from "./shellState.js";
import { sizeClass, trackViewport, viewport } from "./sizeClass.js";
import { onSocket } from "@/services/liveSocket";
import sessionLists from "@/stores/sessionLists.js";
import { resolveMeta } from "@/kit/pageTitle.js";

export default {
  name: "AppShell",
  components: { Sidebar, Rail, SessionListColumn, PaneFoot, OverlayHost, PhoneNav },
  provide() {
    // What a page needs to know about where it sits (Session.vue's back
    // and "Show sidebar" headers).
    return {
      shellLayout: computed(() => ({
        inShell: true,
        mode: this.mode,
        size: this.size,
        density: shellState.density,
        back: () => this.$router.push(lastSessionListPath()).catch(() => {}),
        showSidebar: () => {
          setCollapsed(false);
          this.$router.push(lastSessionListPath()).catch(() => {});
        },
      })),
    };
  },
  data: () => ({ overlayPane: null }),
  computed: {
    width() {
      return viewport.width;
    },
    size() {
      return sizeClass(this.width);
    },
    collapsed() {
      return shellState.collapsed;
    },
    overlay() {
      return overlayKind(this.$route, { density: shellState.density, width: this.width });
    },
    // The route the pane renders: the current one, or under an overlay the
    // last pane route (resolved once per lastPane, so it never remounts).
    paneRoute() {
      return this.overlay ? this.overlayPane : this.$route;
    },
    mode() {
      if (!this.paneRoute) return "pane";
      return shellMode(this.paneRoute, { density: shellState.density, width: this.width });
    },
    showSidebar() {
      return this.size === "desktop" && !this.collapsed && this.mode !== "full";
    },
    showRail() {
      return this.size !== "phone" && this.mode !== "full" && (this.size === "tablet" || this.collapsed);
    },
    showListColumn() {
      return this.mode === "session" && this.size === "desktop" && shellState.density === "pointer";
    },
    listPlace() {
      return shellState.lastSessionPlace;
    },
    openSessionName() {
      const p = this.paneRoute && this.paneRoute.params && this.paneRoute.params.sessionName;
      return typeof p === "string" ? p : "";
    },
    selectedPlace() {
      if (this.overlay || !this.paneRoute) return shellState.lastPlace;
      const place = this.paneRoute.meta && this.paneRoute.meta.place;
      if (place === "@session") return shellState.lastSessionPlace;
      return place || shellState.lastPlace;
    },
    genericPhoneNav() {
      return this.size === "phone" && !!this.paneRoute && !this.paneRoute.meta.ownHeader;
    },
    paneTitle() {
      return this.paneRoute ? resolveMeta(this.paneRoute.meta.title, this.paneRoute) || "" : "";
    },
  },
  watch: {
    $route: {
      handler(route) {
        const overlaid = !!overlayKind(route, { density: shellState.density, width: this.width });
        recordPane(route, { overlay: overlaid || !!(route.meta && route.meta.overlay) });
      },
      immediate: true,
    },
    overlay: {
      handler() {
        this.resolveOverlayPane();
      },
      immediate: true,
    },
    "shellState.lastPane"() {
      if (this.overlay) this.resolveOverlayPane();
    },
  },
  created() {
    this.shellState = shellState;
  },
  mounted() {
    this.cleanups = [];
    this.cleanups.push(trackViewport());
    this.cleanups.push(installDensity(setDensity));
    setShortcutEnv({
      signedIn: () => !!this.$store.state.loggedInUser,
      width: () => viewport.width,
      openSearch: () => this.$router.push("/search").catch(() => {}),
      toggleSidebar: () => setCollapsed(!shellState.collapsed),
    });
    this.cleanups.push(installShortcuts());

    // Signed in: the lists' counts, the stars and the Inbox badge.
    sessionLists.start();
    this.$store.dispatch("loadStarred");
    refreshInboxBadge();
    this.cleanups.push(onSocket("invitation_received", () => refreshInboxBadge()));
    this.cleanups.push(onSocket("invitation_accepted", () => refreshInboxBadge()));
    const onFocus = () => refreshInboxBadge();
    window.addEventListener("focus", onFocus);
    this.cleanups.push(() => window.removeEventListener("focus", onFocus));
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refreshInboxBadge();
    }, 60000);
    this.cleanups.push(() => clearInterval(timer));
  },
  beforeUnmount() {
    for (const fn of this.cleanups || []) {
      try {
        fn();
      } catch (e) {
        /* already gone */
      }
    }
    sessionLists.stop();
    shellState.badges.inbox = 0;
  },
  methods: {
    async resolveOverlayPane() {
      if (!this.overlay) return;
      const target = shellState.lastPane || "/sessions/mine";
      if (this.overlayPane && this.overlayPane.fullPath === target) return;
      try {
        const r = this.$router.resolve(target);
        await loadRouteLocation(r);
        if (this.overlay && (shellState.lastPane || "/sessions/mine") === target) this.overlayPane = r;
      } catch (e) {
        console.error("Couldn't resolve the pane under the overlay", e);
      }
    },
  },
};
</script>

<style scoped>
.app-shell {
  display: flex;
  align-items: stretch;
  min-height: 100dvh;
  width: 100%;
  background: var(--cl-bg);
}
.pane-column {
  flex: 1;
  min-width: 0;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}
.pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* The phone's MiniPlayer floats over the page: pad for it. */
.size--phone .pane {
  padding-bottom: var(--cl-player-h, 0px);
}
.pane > :deep(*) {
  flex: 1 0 auto;
}
</style>
